/**
 * CabinetHub — Batch #14.114 · refonte Batch #14.273 / #14.274 (style Sage/Acomba).
 *
 * Hub "poupée russe" du sidebar > Cabinet.
 * La comptabilité du cabinet utilise exactement les mêmes tuiles et écrans que la Tenue de livres (BOOKS_TILES, AcctSubPage, API /gl) :
 * Master Client (fiche client du cabinet) · Fournisseurs · Transactions clients · Transactions fournisseurs · Grand livre · Rapports · Plan comptable · Produits & services.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, Users, Calculator, CalendarCheck, CalendarClock, ListChecks, Timer, Percent, Globe, LifeBuoy, CalendarRange, TrendingUp } from "lucide-react";
import { api } from "@/api";
import { Badge } from "@/components/shared/Badge";
import { useBadgeCounts } from "@/hooks/useBadgeCounts";
import { BOOKS_TILES } from "./books/_books";
import { TONE_BG } from "@/constants/tones";

const ACCOUNTING = BOOKS_TILES.filter((t) => !t.booksOnly).map((t) => ({ ...t, path: `/admin/comptabilite/${t.id}` }));

const TILES = [
  { id: "master",       title: "Master Client",                desc: "Registre central des clients · n° de client", icon: Users, path: "/admin/masters", tone: "blue" },
  ...ACCOUNTING,
  { id: "cabinet-taxes", title: "Taxes TPS/TVQ",               desc: "Perçues · CTI/RTI · déclarations",  icon: Percent,       path: "/admin/cabinet-taxes",    tone: "indigo" },
  { id: "portail-clients", title: "Portail clients",           desc: "Comptes, accès & tuiles visibles",  icon: Globe,         path: "/admin/portail-clients",  tone: "cyan" },
  { id: "timer",        title: "Timer",                        desc: "Sessions de travail à taux horaire", icon: Timer,        path: "/admin/timer",            tone: "teal" },
  { id: "home-office",  title: "Calculatrice bureau à domicile", desc: "Dépenses d'emploi & T777",        icon: Calculator,    path: "/admin/home-office-calculator", tone: "sky" },
  { id: "conformite",   title: "Calendrier de conformité",     desc: "T2/CO-17 · TPS/TVQ · DAS · T4 · REQ par dossier · rappels J-7 / J-2", icon: CalendarClock, path: "/admin/conformite", tone: "amber" },
  { id: "rentabilite",  title: "Rentabilité par dossier",      desc: "Heures Timer × coût vs facturé · marge · forfait suggéré", icon: TrendingUp, path: "/admin/rentabilite", tone: "emerald" },
  { id: "acomptes",     title: "Mes acomptes provisionnels",  desc: "Travailleur autonome · fédéral + Québec · 3 méthodes · PDF · plan", icon: CalendarRange, path: "/admin/acomptes", tone: "violet" },
  { id: "appointments", title: "Rendez-vous",                  desc: "Planning client",                   icon: CalendarCheck, path: "/admin/appointments",     tone: "rose" },
  { id: "tasks",        title: "Tâches & check-listes",        desc: "Suivi par dossier · étapes à cocher · Kanban", icon: ListChecks, path: "/admin/tasks",      tone: "teal" },
  { id: "aide",         title: "Mode d'emploi et aide",        desc: "Guides pas à pas · feuillets explicatifs · PDF", icon: LifeBuoy, path: "/admin/aide",   tone: "slate" },
];


export default function CabinetHub() {
  const navigate = useNavigate();
  const { counts } = useBadgeCounts();
  const hubBadges = counts?.hubs?.cabinet || {};
  const [drafts, setDrafts] = useState({});
  useEffect(() => { api.get("/gl/overview").then((r) => setDrafts(r.data?.drafts || {})).catch(() => setDrafts({})); }, []);

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-6xl mx-auto" data-testid="cabinet-hub">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 via-blue-50 to-white border-2 border-blue-200 rounded-2xl p-5 sm:p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow">
            <Briefcase className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-slate-900">Cabinet</h1>
            <p className="text-xs text-slate-600 mt-0.5">
              Tous les outils du cabinet — sélectionnez un module pour commencer.
            </p>
          </div>
        </div>
      </div>

      {/* Grille tuiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {TILES.map((t) => {
          const badge = hubBadges[t.id] || 0;
          const nbDraft = (t.drafts || []).reduce((s, k) => s + (drafts[k] || 0), 0);
          return (
            <button
              key={t.id}
              onClick={() => navigate(t.path)}
              className={`relative group bg-gradient-to-br ${TONE_BG[t.tone]} border-2 rounded-2xl p-5 flex flex-col items-center justify-center text-center transition-all duration-200 transform hover:scale-105 active:scale-95 hover:shadow-lg`}
              data-testid={`cabinet-hub-tile-${t.id}`}
            >
              {badge > 0 && (
                <span className="absolute top-2 right-2">
                  <Badge count={badge} testid={`cabinet-hub-badge-${t.id}`} />
                </span>
              )}
              {nbDraft > 0 && <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-500 text-white shadow" title="Pièces non reportées" data-testid={`cabinet-hub-draft-${t.id}`}>{nbDraft}</span>}
              <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-3 group-hover:shadow-md transition">
                <t.icon className="w-7 h-7" />
              </div>
              <span className="font-semibold text-sm mb-0.5">{t.title}</span>
              <span className="text-[10px] opacity-75">{t.desc}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
